import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicModal from "../../Common/DynamicModal";
import RadioInput from "../../DynamicForms/inputs/RadioInput";
import SendMoneyLimitsContent from "./SendMoneyLimitsContent";
import MessageWithDirectivesText from "../../Common/MessagesWithDirectiveText";
import Action from "../../Common/Action";
import { Spinner } from "../../Common/Spinner";
import useGetMobileWalletOperators from "../../../hooks/sendMoney/useGetMobileWalletOperators";
import { SetSelectedOperator } from "../../../actions/sendMoney";
import i18n from "../../../i18n/config";
import ChevronRightBlue from "../../../assets/images/icons/rebrand/chevron-right-blue.svg";
import ChevronDownBlue from "../../../assets/images/icons/rebrand/chevron-down-blue.svg";

// types
import { RootState } from "../../../types/redux";
import { RadioOption } from "../../../types/components/dynamicForms/dynamicField";

const VIEW_WALLET_LIMITS = "VIEW_WALLET_LIMITS";

interface SendMoneyWalletModalProps {
  open: boolean;
  setClose: React.Dispatch<React.SetStateAction<boolean>>;
  quoteId?: number;
}

const SendMoneyWalletModal = ({
  open,
  setClose,
  quoteId,
}: SendMoneyWalletModalProps): React.ReactElement => {
  const dispatch = useDispatch();
  const selectedOperator = useSelector(
    (state: RootState) => state.sendMoney.selectedOperator
  );

  const { loading, operators, disclaimers, primaryDisclaimers, fetchOperators } =
    useGetMobileWalletOperators();

  // transient selection — committed to Redux only on "Continue with X".
  const [selectedCode, setSelectedCode] = useState("");
  // inline limits expand, toggled by the VIEW_WALLET_LIMITS directive.
  const [limitsExpanded, setLimitsExpanded] = useState(false);

  useEffect(() => {
    if (open && quoteId) fetchOperators(quoteId);
  }, [open, fetchOperators, quoteId]);

  // collapse the limits panel whenever the modal reopens
  useEffect(() => {
    if (!open) setLimitsExpanded(false);
  }, [open]);

  // seed on open / when operators arrive: current selection if present, else the
  // first operator (preselected per design). Keyed off operatorCode.
  useEffect(() => {
    if (!open || !operators.length) return;
    const stored = selectedOperator?.operatorCode;
    const isValid = stored && operators.some((op) => op.operatorCode === stored);
    setSelectedCode(isValid ? stored : operators[0].operatorCode);
  }, [open, operators, selectedOperator]);

  const options: RadioOption[] = useMemo(
    () =>
      operators.map((op) => ({
        label: op.name,
        value: op.operatorCode,
        icon: <img src={op.iconUrl} alt="" />,
      })),
    [operators]
  );

  const chosen = operators.find((op) => op.operatorCode === selectedCode);

  const directiveActions = useMemo(
    () => ({ [VIEW_WALLET_LIMITS]: () => setLimitsExpanded((v) => !v) }),
    []
  );

  const onConfirm = () => {
    if (!chosen) return;
    dispatch(SetSelectedOperator(chosen));
    setClose(false);
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex-col align-items-center justify-content-center gap-8">
          <Spinner show className="lds-ring--lg realtime-loading" />
        </div>
      );
    }
    return (
      <>
        <RadioInput
          id="wallet-operator"
          value={selectedCode}
          onChange={setSelectedCode}
          onBlur={() => { }}
          options={options}
        />
        {disclaimers.map((d) => (
          <p
            key={d.disclaimerId}
            className="font-size-sm text-secondary spacing-top-small"
          >
            {d.text}
          </p>
        ))}

        {primaryDisclaimers.map((pd, i) => (
          <div key={i} className="bwp-wallet-limits">
            <MessageWithDirectivesText
              content={pd}
              actions={directiveActions}
              className="font-size-sm text-secondary"
              imgSrc={limitsExpanded ? ChevronDownBlue : ChevronRightBlue}
            />
            {limitsExpanded && (
              <div className="bwp-wallet-limits__panel mt-16">
                <SendMoneyLimitsContent hideSubtitle={true} />
              </div>
            )}
          </div>
        ))}
      </>
    );
  };

  return (
    <DynamicModal
      open={open}
      setClose={setClose}
      showCloseIcon={true}
      className="v3 max-w-350 min-w-350"
      variant="tight"
      header={
        <p className="bold text-primary font-size-lg mb-0">
          {i18n.t("PAGES.SEND_MONEY.TRANSFER_DETAILS.MOBILE_WALLET_MODAL.TITLE")}
        </p>
      }
      headerClassName="mb-8"
      body={renderBody()}
      footer={
        <Action
          onClick={onConfirm}
          disabled={!chosen}
          type="button"
          title={
            chosen
              ? i18n.t("COMMON.CONTINUE_WITH", { name: chosen.name })
              : i18n.t("COMMON.CONTINUE")
          }
          className="w-full"
        />
      }
    />
  );
};

export default SendMoneyWalletModal;